/**
 * DNACORE V4 — Sidebar V4 (Onda 4)
 * Drop-in replacement do <Sidebar2 /> com mesma API:
 *   props: { collapsed, onToggle, pendencias }
 *
 * Re-usa exatamente as mesmas seções de navegação, gates de permissão (legado + V2)
 * e a mesma lógica de carregamento de Mission/Foco. Apenas o LOOK é V4.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useAdminNav } from './adminNavCatalog';
import { usePermV2 } from '../lib/perm_v2';
import './DnaSidebarV4.css';

const API_URL = window.location.origin || '';

// ═══════════════════════════════════════════
// SIDEBAR V4
// ═══════════════════════════════════════════
export default function DnaSidebarV4({ collapsed, onToggle, pendencias = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [mission, setMission] = useState({ pendencias: 0, receita: 0, alunos: 0 });
  const { financeiroVisivel } = usePermV2();
  const filteredSections = useAdminNav();

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const dashRes = await fetch(`${API_URL}/api/v2/dashboard`, { headers: h });
        if (dashRes.ok) {
          const d = await dashRes.json();
          setMission({
            pendencias: d.kpis?.pendencias_total || 0,
            receita: d.kpis?.receita_recebida || 0,
            alunos: d.kpis?.alunos_ativos || 0,
          });
        }
      } catch { /* noop */ }
    })();
  }, []);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  const isSectionActive = useCallback(
    (s) => isActive(s.path) || s.children?.some((c) => !c.divider && isActive(c.path)),
    [isActive]
  );

  useEffect(() => {
    filteredSections.forEach((s) => {
      if (isSectionActive(s)) setExpanded((p) => ({ ...p, [s.id]: true }));
    });
  }, [location.pathname, isSectionActive, filteredSections]);

  const toggleSection = (id) => {
    if (collapsed) {
      onToggle?.();
      setTimeout(() => setExpanded((p) => ({ ...p, [id]: true })), 280);
    } else {
      setExpanded((p) => ({ ...p, [id]: !p[id] }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const fmtK = (v) => (v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}k` : `R$ ${v}`);

  return (
    <aside
      className={`dna4-sb ${collapsed ? 'collapsed' : ''}`}
      data-testid="sidebar-v4"
    >
      {/* ─── BRAND ─── */}
      <div className="dna4-sb-brand">
        <div className="dna4-sb-brand-logo">
          <div className="dna4-sb-brand-mark">D</div>
          <div className="dna4-sb-brand-text">
            <span className="dna4-sb-brand-name">DNACore</span>
            <span className="dna4-sb-brand-ver">V4 · OS</span>
          </div>
        </div>
        {!collapsed && (
          <button
            data-testid="sidebar-collapse-btn"
            className="dna4-sb-toggle"
            onClick={onToggle}
            aria-label="Recolher sidebar"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* ─── MISSION STATUS ─── */}
      {!collapsed && (
        <div className="dna4-sb-mission">
          <div className="dna4-sb-mission-label">DNA Control Status</div>
          <div className="dna4-sb-mission-grid">
            {mission.pendencias > 0 && (
              <button
                data-testid="mission-pendencias"
                className="dna4-sb-mission-cell clickable"
                onClick={() => navigate('/v2/central')}
              >
                <span className="dot" style={{ background: '#fbbf24' }} />
                <span style={{ color: '#fcd34d' }}>{mission.pendencias} pendências</span>
              </button>
            )}
            {financeiroVisivel && (
            <div className="dna4-sb-mission-cell">
              <span className="dot" style={{ background: 'var(--dna-success)' }} />
              <span>{fmtK(mission.receita)}</span>
            </div>
            )}
            <div className="dna4-sb-mission-cell">
              <span className="dot" style={{ background: 'var(--dna-accent-deep)' }} />
              <span>{mission.alunos} alunos</span>
            </div>
            <div className="dna4-sb-mission-cell">
              <span className="dot" style={{ background: 'var(--dna-info)' }} />
              <span>Ativo</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── NAV ─── */}
      <nav className="dna4-sb-nav">
        {filteredSections.map((section) => {
          const Icon = section.icon;
          const active = isSectionActive(section);
          const isOpen = !!expanded[section.id];
          const hasBadge = section.badge === 'pendencias' && pendencias > 0;
          const glow = section.glow || 'var(--dna-accent)';

          return (
            <div key={section.id} style={{ marginBottom: 2 }}>
              <button
                data-testid={`sidebar-nav-${section.id}`}
                onClick={() => toggleSection(section.id)}
                className={`dna4-sb-sec ${active ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                style={{ '--sec-glow': glow }}
              >
                <Icon className="ico" />
                <span className="label">{section.label}</span>
                {hasBadge && (
                  <span className="badge-pill">
                    {pendencias > 99 ? '99+' : pendencias}
                  </span>
                )}
                {!collapsed && <ChevronDown className="chev" />}
              </button>

              {/* Submenu */}
              {!collapsed && isOpen && (
                <div className="dna4-sb-sub is-open">
                  {section.children?.map((child, idx) => {
                    if (child.divider) {
                      return (
                        <div key={`d-${idx}`} className="dna4-sb-sub-divider">
                          {child.label}
                        </div>
                      );
                    }
                    const CI = child.icon;
                    return (
                      <NavLink
                        key={child.path + idx}
                        to={child.path}
                        data-testid={`sidebar-sub-${child.path.replace(/\//g, '-')}`}
                        className={({ isActive: a }) => `dna4-sb-sub-item ${a ? 'active' : ''}`}
                      >
                        <CI className="ico" />
                        <span className="label">{child.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ─── USER ─── */}
      <div className="dna4-sb-user">
        <div className="dna4-sb-user-avatar">
          {(user?.name || user?.nome || 'A').charAt(0).toUpperCase()}
        </div>
        <div className="dna4-sb-user-info">
          <div className="dna4-sb-user-name">{user?.name || user?.nome || 'Administrador'}</div>
          <div className="dna4-sb-user-email">{user?.email || ''}</div>
        </div>
        <button
          data-testid="sidebar-logout-btn"
          className="dna4-sb-user-logout"
          onClick={handleLogout}
          aria-label="Sair"
        >
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Expand FAB */}
      {collapsed && (
        <button
          data-testid="sidebar-expand-btn"
          className="dna4-sb-fab-expand"
          onClick={onToggle}
          aria-label="Expandir sidebar"
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </aside>
  );
}
